import type { FunctionComponent } from 'react';
import { memo } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import type { GeneralFilter } from '~/shared/data-contract/filter';
import { IconArchive, IconLearning } from '~/ui/core/icons';
import { Skeleton } from '~/ui/core/skeleton/Skeleton';
import {
    calculateStatsProgress,
    StatsCount,
    StatsProgress,
    StatsRow,
    statsProgressColors,
} from '~/ui/core/stats/Stats';

import { useLearningOpportunitiesStatisticsQuery } from './api';

type LearningOpportunitiesStatisticsProps = {
    readonly filter?: GeneralFilter;
};

export const LearningOpportunitiesStatistics: FunctionComponent<LearningOpportunitiesStatisticsProps> = memo(
    ({ filter }) => {
        const translate = useTranslate();
        const { data, isLoading } = useLearningOpportunitiesStatisticsQuery({ filter });

        if (isLoading) {
            return (
                <StatsRow className="gap-m">
                    <Skeleton height="24px" width="60px" />
                    <Skeleton height="24px" width="60px" />
                    <Skeleton height="24px" width="60px" />
                </StatsRow>
            );
        }

        if (data === undefined) {
            return null;
        }

        const { archived, draft, published, total } = data;

        return (
            <StatsRow className="flex-nowrap" variant="trigger">
                <StatsCount
                    count={total}
                    data-testid="learning-opportunities-stats-total"
                    tooltip={translate('pages.learning-opportunities.statistics.total-tooltip')}
                >
                    <IconLearning size={12} />
                </StatsCount>
                <StatsProgress
                    colorBase={statsProgressColors.success.base}
                    colorFill={statsProgressColors.success.fill}
                    count={published}
                    data-testid="learning-opportunities-stats-published"
                    icon="publish"
                    progress={calculateStatsProgress(published, total)}
                    tooltip={translate('pages.learning-opportunities.statistics.published-tooltip')}
                />
                <StatsProgress
                    colorBase={statsProgressColors.info.base}
                    colorFill={statsProgressColors.info.fill}
                    count={draft}
                    data-testid="learning-opportunities-stats-draft"
                    icon="review"
                    progress={calculateStatsProgress(draft, total)}
                    tooltip={translate('pages.learning-opportunities.statistics.draft-tooltip')}
                />
                {archived > 0 && (
                    <StatsCount
                        count={archived}
                        data-testid="learning-opportunities-stats-archived"
                        tooltip={translate('pages.learning-opportunities.statistics.archived-tooltip')}
                    >
                        <IconArchive size={12} />
                    </StatsCount>
                )}
            </StatsRow>
        );
    },
);
